<script>
  // UTILS
  import mapboxgl from "mapbox-gl";
  import { onMount, afterUpdate, getContext } from "svelte";
  import { feature } from "topojson-client";

  // COMPONENTS
  import Loading from "./Loading.svelte";
  import Legend from "./Legend.svelte";

  //   CONFIGURING MAP
  let container; // the map container
  let map; // Will be the mapbox instance
  let popup; // The holder for our popup

  export let style = "mapbox://styles/ryanbmarx/ckwk49vgw75pj15n0wt3eijlq";
  export let minZoom = 1;
  export let maxZoom = 16;
  export let zoom = 12; // Initial zoom

  export let center = [-87.603, 41.864]; // The initial view
  export let maxBounds = [
    [-87.676265, 41.869128], // SW: Taylor & Damen
    [-87.579931, 41.926888], // NE: In the lake ... ~ Fullerton
  ];

  // The base color for our ramp
  const OPACITY = 0.9;
  const OPACITY_DURATION = 300;
  const OPACITY_DELAY = 0;

  // This is our color ramp. Should be consistent through all years.
  export let colors = ["#f1eef6", "#bdc9e1", "#74a9cf", "#2b8cbe", "#045a8d"];
  export let bins = [0, 0.2, 0.4, 0.6, 0.8];
  // Pattern is [ value, color to use, ...]
  let stops = colors
    .map((c, i) => {
      return [bins[i], c];
    })
    .flat();

  // Styles for the Cabrini outlines
  let linePaint = {
    "line-color": "#000",
    "line-width": 2,
  };

  let lineLayout = {
    "line-join": "round",
    "line-cap": "round",
  };

  // Keeping track of what the $currentYear and $currentRace _used_ to be
  let previousCurrentYear = null;
  let previousCurrentRace = null;
  let previousActiveLayer = null;

  const { loading, currentYear, currentRace, currentRaceDisplay, races, formatPercent, formatInt } = getContext("bga-race");

  // LAYER MANAGEMENT
  // This will hold IDs of all our added layers
  const layers = [];
  const BUILDINGS = "buildingsLayer";

  // Always know which map layer we want to see.
  $: ACTIVE_ID = `tracts-${$currentYear}-${$currentRace}`;

  // This fires after a state change and the DOM updates
  afterUpdate(() => {
    if (map) setActiveLayer();
  });

  function setActiveLayer() {
    // If anything has changed.
    if (previousActiveLayer != ACTIVE_ID) {
      //  Display the new layer
      map.setPaintProperty(ACTIVE_ID, "fill-opacity", OPACITY);
      // Hide the old layer, if  we have one. (We will not at page load)
      if (previousActiveLayer) map.setPaintProperty(previousActiveLayer, "fill-opacity", 0);

      // Move the now-active layer to the top, underneath the buildings in the z-index
      map.moveLayer(ACTIVE_ID, BUILDINGS);

      // Remember where we _just_ were
      previousCurrentYear = $currentYear;
      previousCurrentRace = $currentRace;
      previousActiveLayer = ACTIVE_ID;
    }
  }

  function tooltip(e) {
    // Change the cursor style as a UI indicator.
    map.getCanvas().style.cursor = "pointer";

    const coordinates = e.features[0].geometry.coordinates.slice();

    // Ensure that if the map is zoomed out such that multiple
    // copies of the feature are visible, the popup appears
    // over the copy being pointed to.
    while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
      coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
    }
    const { properties } = e.features[0];

    let currentRacePercentage = formatPercent(properties[`${$currentYear}_${$currentRace}_per`], 1);
    let currentRacePeople = formatInt(properties[`${$currentYear}_${$currentRace}`]);
    let allResidents = formatInt(properties[`${$currentYear}_total`]);

    let html = `
    <div class="race-popup">
    <p><strong>Census tract ${properties.NAME}</strong></p>
    <p>In ${$currentYear}, <strong>${currentRacePercentage} (${currentRacePeople} people) </strong>of all residents (${allResidents}) were <strong>${$currentRaceDisplay}</strong>.</p>
    </div>`;

    // Populate the popup and set its coordinates
    // based on the feature found.
    popup.setLngLat(e.lngLat).setHTML(html).addTo(map);
  }

  onMount(async () => {
    // Get the data
    const tracts = await fetch("tracts.topojson")
      .then(d => d.json())
      .catch(console.error);

    // Authorize mapbox
    mapboxgl.accessToken = process.env.MAPBOX_TOKEN;

    // Create a popup, but don't add it to the map yet.
    popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
    });

    map = new mapboxgl.Map({
      container,
      style,
      maxZoom,
      minZoom,
      zoom,
      center,
      bounds: [
        [-87.6592287748729, 41.8862341362937],
        [-87.5985243875867, 41.9116734579212],
      ],
      maxBounds,
    });

    map.scrollZoom.disable();
    map.addControl(new mapboxgl.NavigationControl());

    map.on("load", function (e) {
      // ADD DATA SOURCES
      if (!tracts) return;
      map.addSource("tracts2000", {
        type: "geojson",
        data: feature(tracts, "tracts-2000"),
      });

      map.addSource("tracts2020", {
        type: "geojson",
        data: feature(tracts, "tracts-2020"),
      });

      // Add a whole batch of layers for each race
      for (let race of races) {
        // Normalize to our data
        race = race.toLowerCase();

        // Add layer for each of our years
        for (let i = 1970; i <= 2020; i += 10) {
          // e.g. `tracts-1970-white`
          let ID = `tracts-${i}-${race}`;

          // The data source
          let source = i <= 2000 ? "tracts2000" : "tracts2020";

          // Add our layer ID to our list
          layers.push(ID);

          // Make our painting decisions here, based on our scales and stuff
          let paint = {
            "fill-outline-color": "white",
            "fill-opacity-transition": {
              duration: OPACITY_DURATION,
              delay: OPACITY_DELAY,
            },
            "fill-opacity": ID === `tracts-${$currentYear}-${$currentRace}` ? OPACITY : 0,
            "fill-color": ["interpolate", ["linear"], ["get", `${i}_${race}_per`], ...stops],
          };

          // Add layer to map and color it using the stops provided.
          // If it happens to be the layer we want to see at load,
          // then make it visible
          map.addLayer({
            id: ID,
            type: "fill",
            source,
            paint,
          });
        }
      }

      // ----- TOOLTIP -----------------------------
      // Set up the tooltip
      map.on("mousemove", layers, tooltip);

      // Unset the tooltip
      map.on("mouseleave", layers, () => {
        map.getCanvas().style.cursor = "";
        popup.remove();
      });

      // ----- BUILDINGS -----------------------------
      map.addSource("buildings", {
        type: "geojson",
        cluster: false,
        data: feature(tracts, "buildings"),
      });

      map.addLayer({
        id: BUILDINGS,
        type: "fill",
        source: "buildings",
        paint: {
          "fill-opacity": 0.9,
          "fill-color": "black",
        },
      });
      // ----- COMMUNITY AREA -----------------------------
      map.addSource("near-north", {
        type: "geojson",
        cluster: false,
        data: feature(tracts, "near-north"),
      });

      map.addLayer({
        id: "nearNorthLayer",
        type: "line",
        source: "near-north",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#888",
          "line-width": 1,
        },
      });

      // Wait a quarter second to let the actual polygon rendering
      // to catch up. This just makes things look smoother.
      setTimeout(() => {
        $loading = false;
      }, 250);
    });
  });
</script>

<svelte:head>
  <link href="https://api.mapbox.com/mapbox-gl-js/v1.12.0/mapbox-gl.css" rel="stylesheet" />
</svelte:head>
<Legend {stops} />
<div class="wrapper">
  {#if $loading}
    <Loading />
  {/if}
  <div bind:this={container} class="map" />
</div>

<style>
  .wrapper {
    position: relative;
  }

  .map {
    --height: 4;
    --width: 5;
    background: #aaa;
    height: 0;
    padding-bottom: calc(var(--height) * 100% / var(--width));
  }

  @supports (aspect-ratio: 1 /1) {
    .map {
      height: unset;
      padding: unset;
      aspect-ratio: var(--width) / var(--height);
    }
  }
</style>
